import React, { useCallback, useEffect, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Option } from './index';
import Typography from '../typography';
import { useParams } from 'next/navigation';
import classNames from 'classnames';
import { getSkuFromUrlOrSlug, isPdpUrl } from 'helpers/utils/pdpUrlHelper';

export interface Props {
  error?: boolean;
  label?: string;
  required?: boolean;
  labelClassName?: string;
  options?: Option[];
  defaultValue?: Option;
  onChange?: (option: Option) => void;
  selectButtonClassName?: string;
  disableFirstOption?: boolean;
  clearDropdown?: boolean;
  optionsClassName?: any;
  disabled?: boolean;
}

const Select: React.FC<Props> = ({
  error,
  onChange,
  defaultValue,
  label,
  required,
  labelClassName = '',
  selectButtonClassName,
  disableFirstOption = false,
  clearDropdown = false,
  options = [],
  optionsClassName,
  disabled,
}) => {
  const [isChevronRotated, setIsChevronRotated] = useState<boolean>(false);
  const [selected, setSelected] = useState<Option>();

  const { slug } = useParams();

  const [valueDropDown, setValueDropDown] = useState<Option>();
  const isPdp = isPdpUrl(slug as string);
  
  useEffect(() => {
    if (typeof slug === 'string') {
      const variantId = getSkuFromUrlOrSlug(slug);
      const dropDownSelectedValue = options.find((option) => option.value === variantId);
      if (dropDownSelectedValue) {
        setValueDropDown(dropDownSelectedValue);
        setSelected(dropDownSelectedValue);
      } else {
        setValueDropDown(defaultValue);
        setSelected(defaultValue);
      }
    }
  }, [slug, valueDropDown]);

  useEffect(() => {
    if (clearDropdown) {
      setSelected(defaultValue || options[0]);
    }
  }, [clearDropdown, defaultValue, options]);

  const handleChange = useCallback(
    (option: Option) => {
      setSelected(option);
      onChange?.(option);
    },
    [onChange],
  );

  const handleChevronClick = () => {
    setIsChevronRotated(!isChevronRotated);
  };

  const buttonClassNames = useCallback(
    (open?: boolean) => {
      return `${error ? '!border-accent-red' : 'border-black-500'}
        ${
          disabled ? 'cursor-not-allowed' : ' cursor-default cursor-pointer'
        }  relative flex  items-center pl-10  text-left  font-normal text-[#333] border-[#333]
       ${
         isPdp
           ? 'text-16 h-[2.75rem] bg-white py-18 pl-16 pr-32 focus:border-gray-500 active:border-gray-500'
           : ''
       }
        ${selectButtonClassName}`;
    },
    [selectButtonClassName, error],
  );

  return (
    <>
      <Listbox disabled={disabled} value={selected} onChange={handleChange}>
        {({ open }) => (
          <div className="relative w-full">
            {label && (
              <div className="mb-8">
                <span className={`${labelClassName} text-16 font-medium text-secondary-black`}>
                  {/* {required ? `${label} *` : label} */}
                  {label}
                  <span className="!text-red-500">{required && ' *'}</span>
                </span>
              </div>
            )}
            <Listbox.Button className={buttonClassNames(open)} onClick={handleChevronClick}>
              {' '}
              {/* Added onClick event handler */}
              {selected?.imageUrl && (
                <img src={selected.imageUrl} alt={selected.name} className="mr-15  h-32  w-32 rounded-full" />
              )}
              {/* <span className={`${id && !id.includes('-') ? "text-14 lg:text-10 xl:text-10 2xl:text-14 font-semibold leading-[1rem] text-[#333]" : ""}`}>
                {selected?.name}
              </span> */}
              {selected?.itemCode?.endsWith('AF') || selected?.itemCode?.endsWith('LF') ? (
                <div className="flex flex-col">
                  <div
                    className={`${
                      isPdp
                        ? 'text-14 font-semibold text-[#333] lg:text-12 xl:text-12 2xl:text-14'
                        : ''
                    }`}
                  >
                    {selected?.name}
                    {/* {required ? `${selected?.name} *` : `${selected?.name}`} */}
                  </div>

                  <div className="ml-3 text-12 font-semibold lg:mr-5 lg:text-10 xl:text-10 2xl:text-12">
                    (Lead Free)
                  </div>
                </div>
              ) : (
                <div className="w-[165px] xl:w-full">
                  <span
                    className={`${
                      isPdp
                        ? 'md:line-clamp-1 lg:line-clamp-1   pr-8 text-14 font-semibold leading-[1rem] text-[#333] lg:text-12 xl:text-12 2xl:text-14'
                        : ''
                    }`}
                  >
                    {selected?.name}
                    {/* <span className="!text-red-500">{required && ' *'}</span> */}
                  </span>
                </div>
              )}
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-16">
                <ChevronDownIcon
                  className={`h-20 w-20 text-secondary-black ${isChevronRotated ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />{' '}
                {/* Added rotation class */}
              </span>
            </Listbox.Button>
            <Transition
              as={React.Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform origin-top scale-y-0"
              enterTo="transform origin-top scale-y-100"
              leave="transition ease-in duration-100"
              leaveFrom="transform origin-top scale-y-150"
              leaveTo="transform origin-top scale-y-0"
            >
              <Listbox.Options
                className={(active) =>
                  classNames(
                    'opacity-1 absolute top-70 !z-[40] max-h-200 w-full overflow-auto rounded-b-sm bg-white p-10 md:top-70 xl:top-70 2xl:top-70',
                    {
                      'border-x-black-500 border-b-black-500': active,
                      'border-black-400': !active,
                      'lg:!w-[12rem] xl:mt-10 xl:!w-[14rem] 3xl:!w-[19rem]': isPdp,
                    },
                    optionsClassName || 'border-dim-gray',
                  )
                }
              >
                {options.map((option, index) => (
                  <Listbox.Option
                    key={option.value}
                    className={({ active }) =>
                      `${
                        disableFirstOption && index === 0 && 'hidden'
                      }  relative flex cursor-pointer select-none items-center py-16 pl-16 pr-4 text-14 hover:bg-neutral-200 ${
                        active ? 'bg-neutral-200' : ''
                      }`
                    }
                    value={option}
                  >
                    {({ selected }) => (
                      <>
                        {option.imageUrl && (
                          <img
                            src={option.imageUrl}
                            alt={option.name}
                            className="mr-15 h-32 w-32 rounded-full lg:mr-5 xl:mr-15 2xl:mr-15"
                          />
                        )}

                        {option.itemCode?.endsWith('AF') || option.itemCode?.endsWith('LF') ? (
                          <div className="flex flex-col">
                            <div
                              className={`${
                                isPdp
                                  ? 'text-14 font-semibold leading-[1rem] text-[#333] lg:text-12 xl:text-12 2xl:text-14'
                                  : ''
                              }`}
                            >
                              {option.name}
                            </div>
                            <div className="text-12 font-semibold lg:mr-5 lg:text-10 xl:text-10 2xl:text-12">
                              (Lead Free)
                            </div>
                          </div>
                        ) : (
                          <span
                            className={`${
                              isPdp
                                ? 'text-14 font-semibold leading-[1rem] text-[#333] lg:text-12 xl:text-12 2xl:text-14'
                                : ''
                            }`}
                          >
                            {option.name}
                          </span>
                        )}
                        {option.isSelected === true && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="ml-10 lg:ml-2 xl:ml-10 2xl:ml-10"
                            x="0px"
                            y="0px"
                            width="20"
                            height="20"
                            viewBox="0 0 50 50"
                          >
                            <path d="M43.171,10.925L24.085,33.446l-9.667-9.015l1.363-1.463l8.134,7.585L41.861,9.378C37.657,4.844,31.656,2,25,2 C12.317,2,2,12.317,2,25s10.317,23,23,23s23-10.317,23-23C48,19.701,46.194,14.818,43.171,10.925z"></path>
                          </svg>
                        )}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        )}
      </Listbox>
    </>
  );
};

export default Select;
